import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useMediaQuery } from '@mantine/hooks';

// Aligned with Mantine AppShell's `lg` breakpoint (75em ≈ 1200px) so the
// JS-side mobile detection matches the CSS-side navbar collapse breakpoint.
const MOBILE_MEDIA_QUERY = '(max-width: 1200px)';

interface IMenuContext {
  drawerOpen: boolean;
  isMobile: boolean;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  /**
   * Closes the drawer only when the viewport is at or below the mobile
   * breakpoint. A no-op on desktop. Used by sidebar nav items and the
   * clickaway backdrop so neither has to duplicate breakpoint logic.
   */
  closeDrawerIfMobile: () => void;
}

const MenuContext = createContext<IMenuContext | undefined>(undefined);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isMobile = !!useMediaQuery(MOBILE_MEDIA_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const toggleDrawer = useCallback(() => setDrawerOpen((prev) => !prev), []);

  // Keep the drawer closed whenever the viewport drops to mobile size
  // (and reopen it when the user returns to desktop). Mirrors the original
  // effect that previously lived in App.tsx.
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const closeDrawerIfMobile = useCallback(() => {
    if (isMobile) setDrawerOpen(false);
  }, [isMobile]);

  return (
    <MenuContext.Provider
      value={{
        drawerOpen,
        isMobile,
        setDrawerOpen,
        toggleDrawer,
        closeDrawerIfMobile,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) throw new Error('useMenu must be used within a MenuProvider');
  return context;
};
